<template lang="pug">
.fraud-report-container(@click="expand = !expand")
  .fraud-report-summary
    .fraud-report-code
      span.material-symbols-outlined analytics
      strong {{ data.code || ('REPORT-' + data.number) }}

    .fraud-report-trigger
      span.tag {{ triggerLabel }}

    .fraud-report-stats
      span.tag {{ data.ordersAnalyzed || 0 }} analizadas
      span.tag(v-if="data.ordersFlagged") {{ data.ordersFlagged }} marcadas

    .fraud-report-status
      span.tag(:style="statusStyle") {{ statusLabel }}

    .fraud-report-date {{ dateName(data.createdAt) }} - {{ time(data.createdAt) }}

  transition(name="slide-fade")
    .fraud-report-details(v-if="expand" @click.stop="")
      .fraud-report-running(v-if="localStatus === 'running'")
        shLoader(:show="true")
        span Analizando órdenes...

      .fraud-report-error(v-if="localStatus === 'failed'")
        strong Error:
        span {{ localError || data.error || 'Error desconocido' }}

      .fraud-report-meta(v-if="localStatus === 'done'")
        .fraud-meta-row(v-if="data.model") Modelo: {{ data.model }}
        .fraud-meta-row(v-if="data.durationMs") Duración: {{ formatDuration(data.durationMs) }}
        .fraud-meta-row(v-if="data.windowFrom && data.windowTo") Ventana: {{ dateName(data.windowFrom) }} {{ time(data.windowFrom) }} — {{ dateName(data.windowTo) }} {{ time(data.windowTo) }}

      .fraud-report-empty(v-if="localStatus === 'done' && !sortedFindings.length") Sin órdenes sospechosas en este informe.

      .fraud-findings-list(v-if="sortedFindings.length")
        .fraud-finding-row(v-for="finding in sortedFindings" :key="finding.order")
          .fraud-finding-header
            span.tag.fraud-assessment-badge(:style="assessmentStyle(finding.assessment)") {{ assessmentLabel(finding.assessment) }}
            span.tag.fraud-confidence-badge {{ confidenceLabel(finding.confidence) }}
            span.tag.fraud-search-link(@click.stop="openOrderSearch(finding.order)") {{ finding.order }}
            span.fraud-finding-total {{ money(finding.total) }}
            span.fraud-finding-status {{ payStatusLabel(finding) }}

          .fraud-finding-reason {{ finding.reason }}

          ul.fraud-finding-evidence(v-if="finding.evidence && finding.evidence.length")
            li(v-for="(ev, idx) in finding.evidence" :key="idx") {{ ev }}

          .fraud-finding-related(v-if="finding.relatedOrders && finding.relatedOrders.length")
            span Relacionadas:
            span.tag.fraud-related-chip(v-for="rel in finding.relatedOrders" :key="rel" @click.stop="openOrderSearch(rel)") {{ rel }}

          .fraud-finding-case(v-if="finding.caseCode")
            span Caso:
            router-link(:to="`/fraud-cases?originOrder=${finding.order}`") {{ finding.caseCode }}

          .fraud-finding-order-data
            .fraud-finding-contact
              .capitalize {{ finding.name || '—' }}
              .fraud-search-link(@click.stop="openPhoneSearch(finding.phone)") {{ finding.phone || '—' }}
              div {{ finding.email || '—' }}
              div.capitalize {{ finding.location || '—' }}
              div {{ finding.address || '—' }}

            .fraud-finding-pays(v-if="finding.pays && finding.pays.length")
              .fraud-pay-row(v-for="(pay, pidx) in finding.pays" :key="pidx")
                span {{ pay.methodPay || '—' }}
                span(v-if="pay.lastFour") ···{{ pay.lastFour }}
                span(v-if="pay.value") {{ money(pay.value) }}
                span.fraud-pay-state {{ pay.state || '—' }}
                span.fraud-pay-error(v-if="pay.error") {{ pay.error }}

            .fraud-finding-history(v-if="finding.fraudConfirmedCount")
              span(:title="fraudCountTitle(finding.fraudConfirmedCount)") {{ finding.fraudConfirmedCount }}× fraude confirmado
</template>

<script>
import gql from '@my/graphql'
import gqlFraudReport from '@/graphql/fraudReport'
import mFilters from '@/mixins/filters'
import shLoader from '@/components/layout/Loader'
import { fraudCountTitle } from '@/helpers/fraudReview'

const ASSESSMENT_ORDER = { fraud: 0, likely_fraud: 1, suspicious: 2 }

export default {
  name: 'FraudReport',

  mixins: [mFilters],

  components: { shLoader },

  props: {
    data: Object,
    index: Number,
    isNew: Boolean,
    history: Boolean
  },

  data () {
    return {
      expand: false,
      localStatus: this.data.status,
      localError: this.data.error,
      localFindings: this.data.findings || [],
      localOrdersAnalyzed: this.data.ordersAnalyzed,
      localOrdersFlagged: this.data.ordersFlagged,
      localDurationMs: this.data.durationMs,
      pollTimer: null
    }
  },

  computed: {
    sortedFindings () {
      return [...(this.localFindings || [])].sort((a, b) => {
        const rankA = ASSESSMENT_ORDER[a.assessment] !== undefined ? ASSESSMENT_ORDER[a.assessment] : 3
        const rankB = ASSESSMENT_ORDER[b.assessment] !== undefined ? ASSESSMENT_ORDER[b.assessment] : 3
        return rankA - rankB
      })
    },

    triggerLabel () {
      if (this.data.trigger === 'manual') {
        const user = this.data.triggeredBy
        return user ? `Manual — ${user}` : 'Manual'
      }
      return 'Cron'
    },

    statusLabel () {
      if (this.localStatus === 'running') return 'Analizando'
      if (this.localStatus === 'failed') return 'Fallido'
      return 'Completado'
    },

    statusStyle () {
      if (this.localStatus === 'running') return 'background:#2196f3;color:#fff'
      if (this.localStatus === 'failed') return 'background:#d9534f;color:#fff'
      return 'background:#4caf50;color:#fff'
    }
  },

  watch: {
    'data.status' (val) {
      this.localStatus = val
      if (val === 'running') this.startPolling()
      else this.stopPolling()
    }
  },

  mounted () {
    if (this.localStatus === 'running') {
      this.expand = true
      this.startPolling()
    }
  },

  beforeDestroy () {
    this.stopPolling()
  },

  methods: {
    fraudCountTitle,

    assessmentLabel (assessment) {
      const labels = {
        fraud: 'FRAUDE',
        likely_fraud: 'ALTA PROBABILIDAD DE FRAUDE',
        suspicious: 'SOSPECHOSA'
      }
      return labels[assessment] || assessment
    },

    assessmentStyle (assessment) {
      const styles = {
        fraud: 'background:#b71c1c;color:#fff;font-weight:700',
        likely_fraud: 'background:#e65100;color:#fff;font-weight:700',
        suspicious: 'background:#f9a825;color:#fff'
      }
      return styles[assessment] || 'background:#e9e9e7;color:#616161'
    },

    confidenceLabel (confidence) {
      const labels = { high: 'Alta confianza', medium: 'Media confianza', low: 'Baja confianza' }
      return labels[confidence] || confidence
    },

    payStatusLabel (finding) {
      const map = {
        created: 'Creado',
        approved: 'Aprobado',
        paid: 'Pagado',
        packed: 'Empacado',
        sent: 'Enviado',
        delivered: 'Entregado',
        expired: 'Expirado',
        declined: 'Rechazado'
      }
      return map[finding.status] || finding.status || '—'
    },

    formatDuration (ms) {
      const sec = Math.round(ms / 1000)
      if (sec < 60) return `${sec}s`
      const min = Math.floor(sec / 60)
      return `${min}m ${sec % 60}s`
    },

    openOrderSearch (order) {
      if (!order) return
      const route = this.$router.resolve({ path: '/orders', query: { order } })
      window.open(route.href, '_blank')
    },

    openPhoneSearch (phone) {
      if (!phone) return
      const route = this.$router.resolve({ path: '/orders', query: { phone } })
      window.open(route.href, '_blank')
    },

    startPolling () {
      if (this.pollTimer) return
      this.pollTimer = setInterval(() => this.pollReport(), 5000)
    },

    stopPolling () {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },

    async pollReport () {
      if (!this.data.number) return
      try {
        const response = await gql.query(gqlFraudReport.GET_ONE, { number: this.data.number })
        const report = response && response.data && response.data.getFraudReport
        if (!report) return

        this.localStatus = report.status
        this.localError = report.error
        this.localFindings = report.findings || []
        this.localOrdersAnalyzed = report.ordersAnalyzed
        this.localOrdersFlagged = report.ordersFlagged
        this.localDurationMs = report.durationMs

        Object.assign(this.data, report)

        if (report.status !== 'running') {
          this.stopPolling()
          this.expand = true
        }
      } catch (error) {
        console.log(error)
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
.fraud-report-container
  border-bottom 1px solid #e9e9e7
  cursor pointer

  &:hover
    background #fafafa

.fraud-report-summary
  display flex
  align-items center
  flex-wrap wrap
  gap 0.5rem
  padding 0.75rem 0.5rem

.fraud-report-code
  display flex
  align-items center
  gap 0.25rem
  min-width 8rem

.fraud-report-date
  margin-left auto
  font-size 0.85rem
  color #888

.fraud-report-details
  padding 0.5rem 1rem 1rem
  border-top 1px solid #eee

.fraud-report-running
  display flex
  align-items center
  gap 0.75rem
  padding 1rem 0
  color #2196f3

.fraud-report-error
  padding 0.75rem
  background #ffebee
  border-radius 4px
  color #c62828

  strong
    margin-right 0.5rem

.fraud-report-meta
  margin-bottom 0.75rem
  font-size 0.85rem
  color #666

.fraud-meta-row
  margin-bottom 0.25rem

.fraud-report-empty
  padding 0.5rem 0
  color #888

.fraud-finding-row
  border 1px solid #e0e0e0
  border-radius 6px
  padding 0.75rem
  margin-bottom 0.75rem

.fraud-finding-header
  display flex
  align-items center
  flex-wrap wrap
  gap 0.5rem
  margin-bottom 0.5rem

.fraud-assessment-badge
  font-size 0.75rem

.fraud-confidence-badge
  background #e3f2fd
  color #1565c0

.fraud-finding-reason
  margin-bottom 0.5rem
  font-weight 500

.fraud-finding-evidence
  margin 0.5rem 0 0.5rem 1.25rem
  padding 0

  li
    margin-bottom 0.25rem
    font-size 0.9rem

.fraud-finding-related
  margin-bottom 0.5rem
  display flex
  align-items center
  flex-wrap wrap
  gap 0.35rem

.fraud-related-chip
  cursor pointer
  background #fff3e0
  color #e65100

.fraud-finding-case
  margin-bottom 0.5rem
  font-size 0.9rem

.fraud-finding-order-data
  display grid
  grid-template-columns 1fr 1fr
  gap 0.75rem
  font-size 0.85rem
  margin-top 0.5rem
  padding-top 0.5rem
  border-top 1px dashed #eee

.fraud-finding-pays
  .fraud-pay-row
    display flex
    flex-wrap wrap
    gap 0.35rem
    margin-bottom 0.25rem
    font-size 0.8rem

.fraud-pay-error
  color #c62828

.fraud-search-link
  cursor pointer
  text-decoration underline
  color inherit

  &:hover
    opacity 0.8
</style>



// WEBPACK FOOTER //
// src/components/pages/fraudReport/FraudReport.vue